import { useT, type TKey } from "@/lib/i18n";
import { relativeTimeFrom } from "@/lib/services/rule-service";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, cn } from "@fox1ck-org/ui";
import { TemplateScope, TemplateParameterKind, type FlowTemplate } from "@gen/tracker/v1/flow_template_pb";
import { useTrackerRoles } from "@/lib/permissions";
import { MiniBlueprintPreview } from "./mini-preview";

export interface TemplateCardProps {
  template: FlowTemplate;
  onApply: () => void;
  onArchive: () => void;
}

export function TemplateCard({ template, onApply, onArchive }: TemplateCardProps) {
  const navigate = useNavigate();
  const { isPlatformAdmin } = useTrackerRoles();
  const blueprintNodes = template.templateBlueprint?.nodes ?? [];
  const tr = useT();
  const paramCount = template.parameters.length;
  const updated = template.updatedAt ? new Date(template.updatedAt) : null;
  // Mirror the BE writable rule: GLOBAL (curated) templates are editable only
  // by platform admins — hide dead-end Edit/Archive affordances for others.
  const writable =
    template.scope !== TemplateScope.GLOBAL || isPlatformAdmin;

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-ring/40 hover:shadow-[var(--elev-1)]">
      <div className="flex items-start justify-between gap-2 px-4 pt-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ScopeChip scope={template.scope} />
            {template.category ? (
              <span className="rounded-full border border-border bg-muted/30 px-2 py-0.5 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                {template.category}
              </span>
            ) : null}
          </div>
          <h3 className="mt-2 truncate text-base font-semibold text-foreground">
            {template.name}
          </h3>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {template.description || tr("tc.noDesc")}
          </p>
          {template.tags?.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {template.tags.slice(0, 4).map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    background: `color-mix(in oklch, ${t.color || "var(--color-muted-foreground)"}, transparent 84%)`,
                    color: t.color || "var(--color-muted-foreground)",
                  }}
                >
                  {t.name}
                </span>
              ))}
              {template.tags.length > 4 ? (
                <span className="rounded-full bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                  +{template.tags.length - 4}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {writable ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label={tr("tc.actions")}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => navigate(`/admin/flow-templates/${template.id}/edit`)}>
              <Pencil className="h-3.5 w-3.5" />
              {tr("tc.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onArchive}>
              <Trash2 className="h-3.5 w-3.5" />
              {tr("flows.archive")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        ) : null}
      </div>

      <div className="px-4 py-3">
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <MiniBlueprintPreview
            nodes={blueprintNodes}
            edges={template.templateBlueprint?.edges ?? []}
            defaultNodeId={template.templateBlueprint?.defaultNodeId}
          />
        </div>
        {paramCount > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {template.parameters.slice(0, 4).map((p) => (
              <span
                key={p.key}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2 py-0.5 font-mono text-xs text-muted-foreground"
                title={p.description || p.label}
              >
                {paramKindGlyph(p.kind)} {p.label || p.key}
              </span>
            ))}
            {paramCount > 4 ? (
              <span className="rounded-full bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                +{paramCount - 4}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer className="mt-auto flex items-center justify-between gap-3 border-t border-border px-4 py-3">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {tr("aw.nodes", { n: blueprintNodes.length })}
          </span>
          <span>•</span>
          <span className="tabular-nums">
            {tr("tc.params", { n: paramCount })}
          </span>
          {updated ? (
            <>
              <span>•</span>
              <span title={updated.toISOString()}>{relativeTimeFrom(updated)}</span>
            </>
          ) : null}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onApply}
          className="gap-1.5"
        >
          {tr("tc.apply")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </footer>
    </article>
  );
}

function ScopeChip({ scope }: { scope: TemplateScope }) {
  const tr = useT();
  const meta = SCOPE_META[scope] ?? SCOPE_META[TemplateScope.WORKSPACE];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium uppercase tracking-[0.06em]",
        meta.className,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {tr(meta.label)}
    </span>
  );
}

const SCOPE_META: Record<number, { label: TKey; className: string }> = {
  [TemplateScope.WORKSPACE]: {
    label: "tpl.scope.ws",
    className: "border-info/30 bg-info/5 text-info",
  },
  [TemplateScope.ORG]: {
    label: "tpl.scope.org",
    className: "border-warn/30 bg-warn/5 text-warn",
  },
  [TemplateScope.GLOBAL]: {
    label: "tpl.scope.global",
    className: "border-ok/30 bg-ok/5 text-ok",
  },
};

function paramKindGlyph(kind: TemplateParameterKind): string {
  switch (kind) {
    case TemplateParameterKind.OFFER:
      return "◆";
    case TemplateParameterKind.LANDER:
      return "▢";
    case TemplateParameterKind.COUNTRY:
      return "◐";
    case TemplateParameterKind.NUMBER:
      return "#";
    default:
      return "❝";
  }
}

